import { useRef, useState, type FormEvent } from 'react';
import type { WaitlistJoinRequest } from '@signalvote/shared';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { ArrowLeft, Mic, Map, Sparkles, FileCheck, Loader2 } from 'lucide-react';
import { API_BASE_URL, SUBSIDIARY_CODE } from '@/config';
import { getBrandAsset } from '@/themes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const FEATURES = [
  {
    icon: Mic,
    title: 'Voice of the voter',
    description:
      'Record doorstep conversations with voter consent. Hear what they actually said.',
  },
  {
    icon: Map,
    title: 'Field ops',
    description: 'Cut turf, track door-to-door activity.',
  },
  {
    icon: Sparkles,
    title: 'Donor intelligence',
    description: 'Who to ask, and for how much.',
  },
  {
    icon: FileCheck,
    title: 'Finance compliance',
    description: 'Contributions, expenses, filing deadlines.',
  },
];

const EASE = [0.22, 1, 0.36, 1] as const;

const ROLES = ['Candidate', 'Consultant', 'Activist', 'Other'] as const;
type Role = (typeof ROLES)[number];

const PAIN_LABELS = ['Not a pain', 'Minor', 'Noticeable', 'Major', 'Severe'] as const;
type PainLabel = (typeof PAIN_LABELS)[number];
type PainValue = 1 | 2 | 3 | 4 | 5;

const PAIN_QUESTIONS = [
  { key: 'fieldOps' as const, label: 'Field ops' },
  { key: 'finance' as const, label: 'Financial compliance' },
];

const container: Variants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.06, delayChildren: 0.04 },
  },
};

const item: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE } },
};

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function Early() {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const directionRef = useRef<1 | -1>(1);

  const [role, setRole] = useState<Role | null>(null);
  const [roleOther, setRoleOther] = useState('');
  const [fieldOpsPain, setFieldOpsPain] = useState<PainValue | null>(null);
  const [financePain, setFinancePain] = useState<PainValue | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function goForward() {
    directionRef.current = 1;
    setStep((s) => (s < 3 ? ((s + 1) as 1 | 2 | 3) : s));
  }

  function goBack() {
    directionRef.current = -1;
    setStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3) : s));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage(null);

    const body: WaitlistJoinRequest = {
      name,
      email,
      role,
      roleOther: role === 'Other' ? roleOther.trim() : null,
      fieldOpsPain,
      financeCompliancePain: financePain,
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Subsidiary': SUBSIDIARY_CODE,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }

      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  return (
    <main className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-primary/10 via-background to-muted/30">
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="absolute left-0 right-0 top-0 z-10 flex items-center justify-center px-6 py-6 sm:justify-start sm:px-10"
      >
        <img src={getBrandAsset('logo.svg')} alt="" className="h-8 w-auto" />
      </motion.header>

      <div className="relative mx-auto flex min-h-screen w-full max-w-3xl flex-col items-center justify-center px-6 py-24 text-center sm:py-28">
        {status !== 'success' && <StepProgress step={step} />}

        {status !== 'success' && step > 1 && (
          <motion.button
            type="button"
            onClick={goBack}
            aria-label="Go back"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, ease: EASE }}
            className="absolute left-6 top-24 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/70 text-muted-foreground shadow-sm ring-1 ring-border backdrop-blur hover:text-foreground sm:left-10"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </motion.button>
        )}

        <AnimatePresence mode="wait" custom={directionRef.current}>
          {status === 'success' ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="w-full"
            >
              <SuccessPanel name={name} />
            </motion.div>
          ) : step === 1 ? (
            <StepShell key="role" direction={directionRef.current}>
              <RoleStep
                role={role}
                roleOther={roleOther}
                onRoleChange={setRole}
                onRoleOtherChange={setRoleOther}
                onContinue={goForward}
              />
            </StepShell>
          ) : step === 2 ? (
            <StepShell key="pain" direction={directionRef.current}>
              <PainStep
                fieldOpsPain={fieldOpsPain}
                financePain={financePain}
                onFieldOpsChange={setFieldOpsPain}
                onFinanceChange={setFinancePain}
                onContinue={goForward}
              />
            </StepShell>
          ) : (
            <StepShell key="email" direction={directionRef.current}>
              <EmailStep
                name={name}
                email={email}
                status={status}
                errorMessage={errorMessage}
                onNameChange={setName}
                onEmailChange={setEmail}
                onSubmit={handleSubmit}
              />
            </StepShell>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}

function StepProgress({ step }: { step: 1 | 2 | 3 }) {
  return (
    <div
      className="absolute left-1/2 top-20 flex w-40 -translate-x-1/2 items-center gap-2"
      aria-label={`Step ${step} of 3`}
    >
      {[1, 2, 3].map((i) => {
        const active = step === i;
        const complete = step > i;
        return (
          <motion.span
            key={i}
            className={cn(
              'h-1.5 flex-1 rounded-full',
              complete ? 'bg-primary/70' : active ? 'bg-primary' : 'bg-border'
            )}
            animate={{ opacity: active ? 1 : complete ? 0.8 : 0.5 }}
            transition={{ duration: 0.3, ease: EASE }}
          />
        );
      })}
    </div>
  );
}

function StepShell({
  children,
  direction,
}: {
  children: React.ReactNode;
  direction: 1 | -1;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: direction * 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: direction * -24 }}
      transition={{ duration: 0.35, ease: EASE }}
      className="flex w-full flex-col items-center"
    >
      {children}
    </motion.div>
  );
}

function RoleStep({
  role,
  roleOther,
  onRoleChange,
  onRoleOtherChange,
  onContinue,
}: {
  role: Role | null;
  roleOther: string;
  onRoleChange: (r: Role) => void;
  onRoleOtherChange: (v: string) => void;
  onContinue: () => void;
}) {
  const otherValid = roleOther.trim().length >= 2;
  const canContinue = role !== null && (role !== 'Other' || otherValid);

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="flex w-full flex-col items-center">
      <motion.span
        variants={item}
        className="mb-6 inline-flex items-center rounded-full bg-white/70 px-4 py-1.5 text-xs font-medium text-primary shadow-sm ring-1 ring-border backdrop-blur"
      >
        Early access · 1 of 3
      </motion.span>

      <motion.h1
        variants={item}
        className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl"
      >
        Campaigns run better here.
      </motion.h1>

      <motion.p
        variants={item}
        className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg"
      >
        Door-knocking, donors, and compliance — one platform.
      </motion.p>

      <motion.p
        variants={item}
        className="mt-8 text-sm font-medium text-foreground"
      >
        Which sounds like you?
      </motion.p>

      <motion.div
        variants={item}
        className="mt-4 grid w-full max-w-md grid-cols-2 gap-3 sm:grid-cols-4"
      >
        {ROLES.map((r) => {
          const selected = role === r;
          return (
            <button
              key={r}
              type="button"
              onClick={() => onRoleChange(r)}
              className={cn(
                'h-14 rounded-full px-4 text-sm font-semibold shadow-sm ring-1 transition',
                selected
                  ? 'bg-primary text-primary-foreground ring-primary'
                  : 'bg-white/80 text-foreground ring-border backdrop-blur hover:bg-white'
              )}
            >
              {r}
            </button>
          );
        })}
      </motion.div>

      <AnimatePresence initial={false}>
        {role === 'Other' && (
          <motion.div
            key="other-input"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: EASE }}
            className="mt-4 w-full max-w-md overflow-hidden"
          >
            <Input
              aria-label="Tell us what you do"
              autoFocus
              value={roleOther}
              onChange={(e) => onRoleOtherChange(e.target.value)}
              placeholder="Campaign finance firm, nonprofit, etc."
              className="h-14 rounded-full border-border bg-white/80 px-6 text-base shadow-sm backdrop-blur placeholder:text-muted-foreground md:text-base"
              maxLength={120}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div variants={item} className="mt-8 w-full max-w-md">
        <Button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          className="h-14 w-full rounded-full text-base font-semibold shadow-lg"
        >
          Continue
        </Button>
      </motion.div>
    </motion.div>
  );
}

function PainStep({
  fieldOpsPain,
  financePain,
  onFieldOpsChange,
  onFinanceChange,
  onContinue,
}: {
  fieldOpsPain: PainValue | null;
  financePain: PainValue | null;
  onFieldOpsChange: (v: PainValue) => void;
  onFinanceChange: (v: PainValue) => void;
  onContinue: () => void;
}) {
  const canContinue = fieldOpsPain !== null && financePain !== null;
  const valueFor = (key: 'fieldOps' | 'finance') =>
    key === 'fieldOps' ? fieldOpsPain : financePain;
  const setFor = (key: 'fieldOps' | 'finance', v: PainValue) =>
    key === 'fieldOps' ? onFieldOpsChange(v) : onFinanceChange(v);

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="flex w-full flex-col items-center">
      <motion.span
        variants={item}
        className="mb-6 inline-flex items-center rounded-full bg-white/70 px-4 py-1.5 text-xs font-medium text-primary shadow-sm ring-1 ring-border backdrop-blur"
      >
        Early access · 2 of 3
      </motion.span>

      <motion.h1
        variants={item}
        className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl"
      >
        How painful are these today?
      </motion.h1>

      <motion.div variants={item} className="mt-10 flex w-full max-w-xl flex-col gap-6">
        {PAIN_QUESTIONS.map(({ key, label }) => {
          const selected = valueFor(key);
          return (
            <div key={key} className="text-left">
              <p className="mb-3 text-sm font-semibold text-foreground">{label}</p>
              <div className="grid grid-cols-5 gap-2">
                {PAIN_LABELS.map((painLabel, idx) => {
                  const value = (idx + 1) as PainValue;
                  const isOn = selected === value;
                  return (
                    <button
                      key={painLabel}
                      type="button"
                      aria-label={`${label}: ${painLabel}`}
                      onClick={() => setFor(key, value)}
                      className={cn(
                        'flex h-14 flex-col items-center justify-center rounded-2xl px-1 text-[11px] font-medium leading-tight shadow-sm ring-1 transition sm:text-xs',
                        isOn
                          ? 'bg-primary text-primary-foreground ring-primary'
                          : 'bg-white/80 text-foreground ring-border backdrop-blur hover:bg-white'
                      )}
                    >
                      <span className="px-1 text-center">{painLabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </motion.div>

      <motion.div variants={item} className="mt-8 w-full max-w-md">
        <Button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          className="h-14 w-full rounded-full text-base font-semibold shadow-lg"
        >
          Continue
        </Button>
      </motion.div>
    </motion.div>
  );
}

function EmailStep({
  name,
  email,
  status,
  errorMessage,
  onNameChange,
  onEmailChange,
  onSubmit,
}: {
  name: string;
  email: string;
  status: Status;
  errorMessage: string | null;
  onNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
}) {
  return (
    <motion.div variants={container} initial="hidden" animate="show" className="flex w-full flex-col items-center">
      <motion.span
        variants={item}
        className="mb-6 inline-flex items-center rounded-full bg-white/70 px-4 py-1.5 text-xs font-medium text-primary shadow-sm ring-1 ring-border backdrop-blur"
      >
        Early access · 3 of 3
      </motion.span>

      <motion.h1
        variants={item}
        className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl"
      >
        Replace the spreadsheet.
        <br />
        Win the race.
      </motion.h1>

      <motion.p
        variants={item}
        className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg"
      >
        You&apos;re almost in. Drop your details and we&apos;ll email you.
      </motion.p>

      <motion.form
        variants={item}
        onSubmit={onSubmit}
        className="mt-10 w-full max-w-md space-y-3"
        noValidate
      >
        <Input
          aria-label="Name"
          type="text"
          autoComplete="name"
          required
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          disabled={status === 'submitting'}
          placeholder="Full name"
          className="h-14 rounded-full border-border bg-white/80 px-6 text-base shadow-sm backdrop-blur placeholder:text-muted-foreground md:text-base"
        />

        <Input
          aria-label="Email"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          disabled={status === 'submitting'}
          placeholder="Enter your email"
          className="h-14 rounded-full border-border bg-white/80 px-6 text-base shadow-sm backdrop-blur placeholder:text-muted-foreground md:text-base"
        />

        <Button
          type="submit"
          disabled={status === 'submitting'}
          className="h-14 w-full rounded-full text-base font-semibold shadow-lg"
        >
          {status === 'submitting' ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Joining…
            </>
          ) : (
            'Join the waitlist'
          )}
        </Button>

        {errorMessage && (
          <motion.p
            role="alert"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: EASE }}
            className="text-sm text-destructive"
          >
            {errorMessage}
          </motion.p>
        )}
      </motion.form>

      <motion.p variants={item} className="mt-4 text-xs text-muted-foreground">
        No spam. We&apos;ll email you when early access opens.
      </motion.p>

      <motion.div variants={item} className="mt-16 w-full max-w-xl">
        <p className="mb-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          What you&apos;ll get access to
        </p>
        <ul className="grid gap-6 text-left sm:grid-cols-2">
          {FEATURES.map(({ icon: Icon, title, description }) => (
            <li key={title} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-tight text-foreground">{title}</p>
                <p className="mt-1 text-xs leading-snug text-muted-foreground">{description}</p>
              </div>
            </li>
          ))}
        </ul>
      </motion.div>
    </motion.div>
  );
}

function SuccessPanel({ name }: { name: string }) {
  const firstName = name.trim().split(/\s+/)[0] || 'there';
  return (
    <div className="flex flex-col items-center text-center">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.45, ease: EASE, delay: 0.1 }}
        className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"
      >
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-7 w-7"
          aria-hidden="true"
          initial="hidden"
          animate="visible"
        >
          <motion.polyline
            points="20 6 9 17 4 12"
            variants={{
              hidden: { pathLength: 0, opacity: 0 },
              visible: {
                pathLength: 1,
                opacity: 1,
                transition: { duration: 0.5, ease: EASE, delay: 0.3 },
              },
            }}
          />
        </motion.svg>
      </motion.div>
      <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
        You&apos;re on the list, {firstName}.
      </h1>
      <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
        We&apos;ll be in touch the moment early access opens up.
      </p>
    </div>
  );
}
